import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";
import { api } from "../../../../redux/apis/api";

// Reusable async product search-and-select.
//
// Hits GET /product/get?field=item_name_search&value=<query> (the existing
// trigram-similarity search the billing screen uses), debounces typing,
// renders results in a dropdown, and emits the chosen product as
//   { product_id, item_code, name, mrp }
// where mrp falls back to 0 if no pricing column is present.
//
// Usage:
//   <ProductPicker
//     value={{ product_id, name }}
//     onSelect={({ product_id, item_code, name, mrp }) => ... }
//     onClear={() => ... }
//     placeholder="Type a product name..."
//   />
//
// Used in scheme create-tab for free_slab materials, disc_material refs, and
// combo combo_items so authors don't paste raw ULIDs.

const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;
const MAX_RESULTS = 12;

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

const extractMrp = (item) =>
  Number(
    item?.item_mrp_price ??
      item?.mrp_price ??
      item?.mrp ??
      item?.item_unit_price ??
      item?.new_printed_mrp ??
      0
  );

const ProductPicker = ({
  value,            // { product_id, name } or null
  onSelect,         // ({product_id, item_code, name, mrp}) => void
  onClear,          // () => void  (optional; falls back to onSelect(null))
  placeholder = "Search product by name…",
  disabled = false,
  mono = false,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const lastCallId = useRef(0);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      setResults([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const callId = ++lastCallId.current;
      setLoading(true);
      try {
        const res = await api.get(
          `/product/get?field=item_name_search&value=${encodeURIComponent(q)}`
        );
        if (callId !== lastCallId.current) return;
        const rows = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setResults(rows.slice(0, MAX_RESULTS));
        setHighlight(0);
      } catch (err) {
        if (callId !== lastCallId.current) return;
        console.warn("[ProductPicker] search failed:", err?.response?.data || err?.message);
        setResults([]);
      } finally {
        if (callId === lastCallId.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const displayValue = useMemo(() => {
    if (value?.name && value?.product_id) {
      return value.item_code ? `${value.name} · #${value.item_code}` : value.name;
    }
    if (value?.product_id) return value.product_id;
    return "";
  }, [value]);

  const pick = (item) => {
    onSelect({
      product_id: item.item_id,
      item_code: item.item_code,
      name: item.item_name,
      mrp: round2(extractMrp(item)),
    });
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  const clear = () => {
    if (onClear) onClear();
    else onSelect(null);
    setQuery("");
    setResults([]);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[highlight]) pick(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const hasValue = !!value?.product_id;

  return (
    <div className="relative" ref={wrapRef}>
      {/* Selected chip OR search input */}
      {hasValue && !open ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(true)}
          className={`w-full flex items-center gap-2 px-2.5 py-1.5 border border-[#E6E9EF] rounded bg-white hover:bg-[#F8FAFC] text-left ${
            disabled ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          <Check size={12} className="text-[#15803C] shrink-0" />
          <span className={`flex-1 truncate text-[12.5px] text-[#0F172A] ${mono ? "font-mono" : ""}`}>
            {displayValue}
          </span>
          {!disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                clear();
              }}
              className="text-[#94A3B8] hover:text-[#B91C1C]"
              title="Clear"
            >
              <X size={12} />
            </span>
          )}
        </button>
      ) : (
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            type="text"
            value={query}
            disabled={disabled}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="w-full pl-7 pr-7 py-1.5 text-[12.5px] border border-[#E6E9EF] rounded focus:outline-none focus:border-primary"
          />
          {loading && (
            <Loader2 size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] animate-spin" />
          )}
          {!loading && (
            <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          )}
        </div>
      )}

      {/* Dropdown panel */}
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#E6E9EF] rounded shadow-lg max-h-[280px] overflow-y-auto">
          {query.trim().length < MIN_QUERY ? (
            <div className="px-3 py-2 text-[11.5px] text-[#94A3B8]">
              Type at least {MIN_QUERY} characters
            </div>
          ) : loading ? (
            <div className="px-3 py-2 text-[11.5px] text-[#64748B] flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-[11.5px] text-[#94A3B8]">
              No products match “{query.trim()}”
            </div>
          ) : (
            results.map((item, i) => {
              const mrp = round2(extractMrp(item));
              const isHi = i === highlight;
              return (
                <button
                  key={item.item_id}
                  type="button"
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => pick(item)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 border-b border-[#F1F5F9] last:border-b-0 ${
                    isHi ? "bg-[#F8FAFF]" : "hover:bg-[#FAFBFC]"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#0F172A] truncate">
                      {item.item_name}
                    </div>
                    <div className="text-[11px] text-[#64748B] flex items-center gap-1.5">
                      {item.item_code != null && (
                        <span className="font-mono">#{item.item_code}</span>
                      )}
                      <span className="font-mono opacity-70 truncate">{item.item_id}</span>
                    </div>
                  </div>
                  {mrp > 0 && (
                    <div className="text-[11.5px] font-semibold text-[#15803C] tabular-nums">
                      ₹{mrp}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ProductPicker;
